import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRole } from "@/contexts/RoleContext";
import type { AppRole } from "@/contexts/RoleContext";
import { biensRepository } from "@/repositories/biensRepository";
import type { BienRow, BienUpdate } from "@/schemas/bien";
import type { BienContact } from "@/schemas/bienContact";

/**
 * Clés de cache React Query — centralisées pour pouvoir invalider ciblé.
 */
export const biensKeys = {
  all: ["biens"] as const,
  list: (role: string) => ["biens", "list", role] as const,
  detail: (role: string, id: string) => ["biens", "detail", role, id] as const,
};

/**
 * Liste tous les biens accessibles au rôle courant.
 *
 * React Query :
 * - Cache-keyé sur le rôle (si le rôle change, refetch auto)
 * - staleTime / gcTime par défaut de queryClient.ts (30s / 5min)
 *
 * `roleOverride` : optionnel — utiliser un rôle différent du rôle courant
 * pour le RPC de lecture. Ex. /apparts-for-rent applique la règle
 * "mailing-ready" (validateBienPrep) qui a besoin des champs C-Level :
 * sans override, le collaborateur n'aurait que ~49 champs sur 93 et la
 * validation échouerait pour tous les biens.
 */
export function useBiens(roleOverride?: AppRole) {
  const { role: ctxRole } = useRole();
  const role = roleOverride ?? ctxRole;
  return useQuery<BienRow[]>({
    queryKey: biensKeys.list(role),
    queryFn: () => biensRepository.list(role),
  });
}

/**
 * Détail d'un bien. `id` peut être undefined pour désactiver la requête.
 *
 * `roleOverride` : optionnel — utiliser un rôle différent du rôle courant
 * pour le RPC de lecture. Sinon les champs visibles côté UI mais invisibles
 * pour le rôle de base sont strippés du résultat → après save+refetch le
 * form reset à la valeur par défaut, donnant l'impression que le save
 * n'a pas pris.
 *
 * ⚠️ `effective_role()` (migration 099) n'accepte le rôle demandé QUE s'il est
 * de la famille de l'appelant, et retombe SILENCIEUSEMENT sur le rôle réel
 * sinon. Un override hors famille ne lève donc aucune erreur : il ne fait
 * rien. Seul usage restant : `/publi-temp`, qui force `admin`.
 */
export function useBien(id: string | undefined, roleOverride?: AppRole) {
  const { role: ctxRole } = useRole();
  const role = roleOverride ?? ctxRole;
  return useQuery<BienRow | null>({
    queryKey: id ? biensKeys.detail(role, id) : ["biens", "detail", role, "__none__"],
    queryFn: () => (id ? biensRepository.get(role, id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

/**
 * Contact (email/téléphone) des 3 personnes liées à un bien précis
 * (collaborateurSrs/expertPeb/prestataireEle) — via le RPC scopé
 * `get_bien_contacts` (migration 126), jamais via `useProfiles()` qui ne
 * renvoie plus ces champs pour les rôles non-admin. `id` peut être undefined
 * pour désactiver la requête.
 */
export function useBienContacts(id: string | undefined) {
  return useQuery<BienContact[]>({
    queryKey: ["biens", "contacts", id ?? "__none__"],
    queryFn: () => (id ? biensRepository.getContacts(id) : Promise.resolve([])),
    enabled: !!id,
  });
}

/**
 * Mutation UPDATE d'un bien. Après succès :
 * - Met à jour directement le cache du détail (évite le flash)
 * - Invalide la liste avec `refetchType: "all"` pour que le refetch parte
 *   aussi sur les queries inactives (liste non-montée à ce moment), sinon
 *   l'utilisateur revoit des données périmées à son prochain passage sur
 *   /biens.
 *
 * `roleOverride` : optionnel — utiliser un rôle différent du rôle courant pour
 * le RPC, afin que les `can_edit` utilisées par `update_entity_with_perms`
 * matchent celles affichées par `useFieldPermissions`. Sans cet override,
 * l'UI peut autoriser une édition que le RPC rejette ("Aucun champ autorisé
 * à la modification pour le rôle X"). Il doit TOUJOURS valoir le même rôle que
 * celui passé à `useBien` et à `useFieldPermissions` sur le même écran.
 */
export function useUpdateBien(id: string, roleOverride?: AppRole) {
  const { role: ctxRole } = useRole();
  const role = roleOverride ?? ctxRole;
  const qc = useQueryClient();
  return useMutation({
    // Filet global (audit 2026-08-07) : c'est LA mutation derrière chaque
    // sauvegarde de champ des deux fiches. Un refus de la base — garde-fou de
    // publication 082, permissions, RLS — était totalement silencieux. Cf.
    // src/lib/queryClient.ts + src/lib/mutationErrorBridge.ts.
    meta: { toastOnError: true },
    mutationFn: (patch: BienUpdate) => biensRepository.update(role, id, patch),
    onSuccess: (updated) => {
      qc.setQueryData(biensKeys.detail(ctxRole, id), updated);
      // Invalide TOUTES les list queries (peu importe le role en cache) — sinon
      // les listes ouvertes sous un autre role (ex : user passe d admin a c_level
      // simule) restent sur du cache stale. Le predicate filter cote front est
      // recalcule a chaque render apres refetch.
      // refetchType: "all" couvre meme les queries non montees (navigation).
      qc.invalidateQueries({
        queryKey: biensKeys.all,
        refetchType: "all",
      });
    },
  });
}

/**
 * Mutation pour marquer un bien comme "publié explicitement" — bump
 * `last_published_at = now()` via RPC SECURITY DEFINER (cf. migration 045).
 *
 * Décision produit 2026-05-07 : la pastille de publication ne devient verte
 * qu'après ce clic explicite. Tant que `last_published_at` est NULL ou plus
 * ancien que `updated_at`, la pastille reste orange "à pousser".
 */
export function useMarkBienAsPublished(id: string) {
  const { role: ctxRole } = useRole();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => biensRepository.markAsPublished(id),
    onSuccess: (updated) => {
      qc.setQueryData(biensKeys.detail(ctxRole, id), updated);
      qc.invalidateQueries({
        queryKey: biensKeys.all,
        refetchType: "all",
      });
    },
  });
}

/**
 * Mutation DELETE d'un bien. Admin-only côté DB (RPC delete_bien vérifie
 * p_role='admin'). Après succès :
 *  - Retire le détail du cache (sinon useBien retournerait l'objet périmé)
 *  - Invalide la liste pour rafraîchir l'écran /biens
 */
export function useDeleteBien(role?: string) {
  const { role: ctxRole } = useRole();
  const effectiveRole = role ?? ctxRole;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => biensRepository.delete(effectiveRole, id),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: biensKeys.detail(ctxRole, id) });
      if (effectiveRole !== ctxRole) {
        qc.removeQueries({ queryKey: biensKeys.detail(effectiveRole, id) });
      }
      qc.invalidateQueries({
        queryKey: biensKeys.all,
        refetchType: "all",
      });
    },
  });
}

export function useCreateBien(role?: string) {
  const { role: ctxRole } = useRole();
  const effectiveRole = role ?? ctxRole;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof biensRepository.create>[1]) =>
      biensRepository.create(effectiveRole, data),
    onSuccess: (created) => {
      // Hydrate le cache du détail avec la ligne fraîchement insérée — sinon
      // `useBien(id)` continue à renvoyer null jusqu'au prochain refetch, et
      // le BienDetailPage retente un CREATE au lieu d'un UPDATE → 409 Conflict
      // sur l'id (déjà inséré).
      if (created?.id) {
        qc.setQueryData(biensKeys.detail(ctxRole, created.id), created);
        if (effectiveRole !== ctxRole) {
          qc.setQueryData(biensKeys.detail(effectiveRole, created.id), created);
        }
      }
      qc.invalidateQueries({
        queryKey: biensKeys.all,
        refetchType: "all",
      });
    },
  });
}
